import { useCallback, useEffect, useState } from "react";

/**
 * Shows something after a delay, once per browser session.
 *
 * `sessionStorage` is read and written through try/catch: it throws outright in
 * some privacy modes and when storage is blocked, and a promo popout must never
 * be the reason a page fails to render. If storage is unavailable the popout
 * simply behaves as "every load" rather than breaking.
 */
export function useDismissible(key: string, delayMs: number) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let alreadyDismissed = false;
    try {
      alreadyDismissed = window.sessionStorage.getItem(key) === "1";
    } catch {
      // storage blocked — fall through and show it
    }
    if (alreadyDismissed) return;

    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [key, delayMs]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      window.sessionStorage.setItem(key, "1");
    } catch {
      // storage blocked — it will reappear next load, which is acceptable
    }
  }, [key]);

  return { visible, dismiss };
}
